import { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  Loader2, 
  RefreshCw, 
  Plus, 
  Search, 
  Building2, 
  Trash2,
  Edit2,
  Check,
  X,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Account types based on Xero
const ACCOUNT_TYPES = [
  { value: 'ASSET', label: 'Asset', class: 'ASSET' },
  { value: 'BANK', label: 'Bank', class: 'ASSET' },
  { value: 'CURRENT', label: 'Current Asset', class: 'ASSET' },
  { value: 'CURRLIAB', label: 'Current Liability', class: 'LIABILITY' },
  { value: 'DEPRECIATN', label: 'Depreciation', class: 'EXPENSE' },
  { value: 'DIRECTCOSTS', label: 'Direct Costs', class: 'EXPENSE' },
  { value: 'EQUITY', label: 'Equity', class: 'EQUITY' },
  { value: 'EXPENSE', label: 'Expense', class: 'EXPENSE' },
  { value: 'FIXED', label: 'Fixed Asset', class: 'ASSET' },
  { value: 'LIABILITY', label: 'Liability', class: 'LIABILITY' },
  { value: 'NONCURRENT', label: 'Non-current Asset', class: 'ASSET' },
  { value: 'OTHERINCOME', label: 'Other Income', class: 'REVENUE' },
  { value: 'OVERHEADS', label: 'Overhead', class: 'EXPENSE' },
  { value: 'PREPAYMENT', label: 'Prepayment', class: 'ASSET' },
  { value: 'REVENUE', label: 'Revenue', class: 'REVENUE' },
  { value: 'SALES', label: 'Sales', class: 'REVENUE' },
  { value: 'TERMLIAB', label: 'Non-current Liability', class: 'LIABILITY' },
];

const ACCOUNT_CLASSES = [
  { value: 'ASSET', label: 'Assets', color: 'text-blue-400' },
  { value: 'LIABILITY', label: 'Liabilities', color: 'text-red-400' },
  { value: 'EQUITY', label: 'Equity', color: 'text-purple-400' },
  { value: 'REVENUE', label: 'Revenue', color: 'text-green-400' },
  { value: 'EXPENSE', label: 'Expenses', color: 'text-orange-400' },
];

export default function ChartOfAccounts() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [lastImport, setLastImport] = useState(null);
  
  // Create/Edit dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: 'EXPENSE',
    description: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAccounts();
    checkXeroStatus();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data.accounts || []);
      setLastImport(response.data.last_import);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkXeroStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/xero/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setXeroConnected(response.data.connected);
    } catch (error) {
      console.error('Failed to check Xero status:', error);
    }
  };

  const handleImportFromXero = async () => {
    if (!xeroConnected) {
      toast.error('Please connect Xero first in Settings');
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cointracker/xero/import-accounts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Imported ${response.data.imported} accounts from Xero`);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import from Xero');
    } finally {
      setImporting(false);
    }
  };

  const openCreateDialog = () => {
    setEditingAccount(null);
    setFormData({ code: '', name: '', type: 'EXPENSE', description: '' });
    setShowDialog(true);
  };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    setFormData({
      code: account.code || '',
      name: account.name || '',
      type: account.type || 'EXPENSE',
      description: account.description || ''
    });
    setShowDialog(true);
  };

  const handleSaveAccount = async () => {
    if (!formData.code || !formData.name) {
      toast.error('Code and Name are required');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingAccount) {
        // Update existing
        await axios.put(`${API_URL}/cointracker/accounts/${editingAccount.code}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Account updated');
      } else {
        // Create new
        await axios.post(`${API_URL}/cointracker/accounts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Account created');
      }
      
      setShowDialog(false);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (code) => {
    if (!confirm(`Delete account ${code}? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/cointracker/accounts/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account deleted');
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  // Filter accounts
  const filteredAccounts = accounts.filter(acc => {
    const matchesSearch = 
      acc.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      acc.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesClass = filterClass === 'all' || acc.class === filterClass;
    return matchesSearch && matchesClass;
  });

  // Group accounts by class
  const groupedAccounts = ACCOUNT_CLASSES.map(cls => ({
    ...cls,
    accounts: filteredAccounts.filter(acc => acc.class === cls.value)
  })).filter(group => group.accounts.length > 0);

  const getTypeLabel = (type) => {
    const found = ACCOUNT_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Chart of Accounts</h2>
          <p className="text-sm text-zinc-400 mt-1">
            {accounts.length} accounts
            {lastImport && (
              <span className="ml-2 text-zinc-500">
                • Last synced: {new Date(lastImport).toLocaleDateString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleImportFromXero}
            disabled={importing || !xeroConnected}
            className="bg-[#13B5EA] text-white hover:bg-[#0fa8db] disabled:opacity-50"
            title={!xeroConnected ? 'Connect Xero in Settings first' : 'Import accounts from Xero'}
          >
            {importing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Import from Xero
          </Button>
          <Button
            onClick={openCreateDialog}
            variant="outline"
            className="border-zinc-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Account
          </Button>
        </div>
      </div>

      {/* Xero Status Banner */}
      {!xeroConnected && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-amber-400" />
              <span className="text-amber-200 text-sm">
                Connect Xero in Settings to sync your Chart of Accounts automatically
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search by code or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700 text-white">
            <SelectValue placeholder="All Classes" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all" className="text-white">All Classes</SelectItem>
            {ACCOUNT_CLASSES.map(cls => (
              <SelectItem key={cls.value} value={cls.value} className="text-white">
                {cls.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Accounts List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      ) : accounts.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12">
            <div className="text-center">
              <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No accounts yet</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Import your Chart of Accounts from Xero or create accounts manually
              </p>
              <div className="flex justify-center gap-3">
                <Button
                  onClick={handleImportFromXero}
                  disabled={!xeroConnected}
                  className="bg-[#13B5EA] text-white hover:bg-[#0fa8db]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Import from Xero
                </Button>
                <Button onClick={openCreateDialog} variant="outline" className="border-zinc-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Manually
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupedAccounts.map(group => (
            <Card key={group.value} className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className={`text-lg ${group.color}`}>
                  {group.label}
                  <span className="text-zinc-500 text-sm font-normal ml-2">
                    ({group.accounts.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Code</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Name</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Type</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Source</th>
                        <th className="text-right py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.accounts.map(account => (
                        <tr 
                          key={account.code} 
                          className="border-b border-zinc-800/50 hover:bg-zinc-800/30"
                        >
                          <td className="py-3 px-3">
                            <span className="font-mono text-white">{account.code}</span>
                          </td>
                          <td className="py-3 px-3">
                            <span className="text-white">{account.name}</span>
                            {account.description && (
                              <p className="text-xs text-zinc-500 mt-0.5">{account.description}</p>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            <span className="text-zinc-400 text-sm">{getTypeLabel(account.type)}</span>
                          </td>
                          <td className="py-3 px-3">
                            {account.xero_account_id ? (
                              <span className="px-2 py-0.5 text-xs rounded bg-[#13B5EA]/20 text-[#13B5EA] border border-[#13B5EA]/30">
                                Xero
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs rounded bg-zinc-700 text-zinc-400">
                                Manual
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(account)}
                                className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              {!account.xero_account_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAccount(account.code)}
                                  className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAccount ? 'Edit Account' : 'Add Account'}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {editingAccount 
                ? 'Update the account details below'
                : 'Create a new account for your Chart of Accounts'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Account Code *</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="e.g., 1200"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  disabled={!!editingAccount}
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Type *</label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                    {ACCOUNT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Account Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Crypto Assets"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDialog(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAccount}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {editingAccount ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
